/** Workflows P5-8 (M26): the "Attribute changed" and "Visitor clicks an element" triggers, who changed a status, and staff editing attribute values. Merged into `engage.workflows`. */
export const workflowsTriggers = {
  groups: { data: "Data changes", service: "Service desk", orders: "Orders and quotes", field_service: "Field service" },
  exits: {
    ticketStepDone: "Ticket step worked",
    ticketStepFailed: "Ticket step did not work",
    ticketAssigned: "Given to a person",
    ticketQueued: "Waiting in the queue",
    clientTold: "Client was told",
    clientNotTold: "Client was not told",
    assetFound: "Asset found",
    assetNotFound: "No such asset for this client",
    productsFound: "Products found",
    productsNotFound: "No product matches",
    ticketSubmitted: "Request sent",
    ticketDuplicateUsed: "Added to an open request",
    ticketAbandoned: "Client left or the card closed",
    ticketNeedsPerson: "A person should take over",
    ticketNotSubmitted: "Form not sent",
    ticketsShown: "Requests shown",
    ticketsNotVerified: "Client not verified",
    productAdded: "Added to the cart",
    catalogueClosed: "Card closed",
    orderPlaced: "Order placed",
    orderForApproval: "Sent for approval",
    orderCancelled: "Client cancelled or the review closed",
    orderFailed: "Order not placed",
    ordersShown: "Orders shown",
    ordersNotVerified: "Client not signed in",
  },
  kinds: { widgetOnly: "Website widget only" },
  triggers: {
    ticket_created: "Ticket created",
    ticket_updated: "Ticket updated",
    ticket_state_changed: "Ticket status changed",
    ticket_assigned: "Ticket assigned",
    ticket_returned_to_queue: "Ticket returned to the queue",
    ticket_customer_replied: "Client replied",
    ticket_staff_replied: "Team replied",
    ticket_note_added: "Internal note added",
    ticket_sla_warning: "Ticket close to its SLA target",
    ticket_sla_breached: "Ticket missed its SLA target",
    ticket_unassigned_due: "Ticket still unassigned",
    ticket_waiting_reminder_due: "Reminder due for a waiting ticket",
    ticket_pending_expired: "Waiting time over",
    ticket_resolved: "Ticket resolved",
    ticket_reopened: "Ticket reopened",
    ticket_auto_close_due: "Ready to close",
    ticket_csat_received: "Rating received",
    ticket_intake_requested: "Client wants to open a ticket",
    order_placed: "Order placed",
    order_status_changed: "Order status changed",
    order_approval_requested: "Order needs an approval",
    order_approval_decided: "Order approval decided",
    order_sync_failed: "Order could not be sent",
    quote_status_changed: "Quote status changed",
    visit_scheduled: "Visit scheduled",
    visit_status_changed: "Visit status changed",
    visit_reminder_due: "Visit reminder due",
    asset_maintenance_due: "Maintenance due",
    asset_warranty_expiring: "Warranty ending",
    contract_expiring: "Contract ending",
    rma_status_changed: "Return status changed",
    attribute_changed: "Attribute changed",
    visitor_clicks_element: "Visitor clicks an element",
    platform_event: "Any event your endpoints receive",
    handoff_reminder_due: "Handoff reminder is due",
    handoff_escalation_due: "Handoff escalation is due",
    handoff_waiting_digest_due: "After-hours handoffs are ready to report",
    conversation_returned_to_queue: "Conversation returned to the queue",
    lead_followed_up: "Lead asked again",
    gap_assigned: "Knowledge gap assigned",
    connector_event_received: "Event from a connected system",
  },
  triggerHints: {
    ticket_created: "Start when a ticket is created, from any door.",
    ticket_updated: "React when a ticket's title, type, priority, tags, answers or due date change.",
    ticket_state_changed: "React when a ticket moves to another status.",
    ticket_assigned: "React when a ticket's team or assignee changes.",
    ticket_returned_to_queue: "Alert the team when a member leaves and their tickets go back to the queue.",
    ticket_customer_replied: "React when the client writes on a ticket.",
    ticket_staff_replied: "React when your team answers a client on a ticket.",
    ticket_note_added: "React when somebody adds an internal note, and to who was mentioned.",
    ticket_sla_warning: "Warn before a first-response or resolution target is missed.",
    ticket_sla_breached: "React when a first-response or resolution target is missed.",
    ticket_unassigned_due: "Escalate a ticket nobody has picked up after the waiting time.",
    ticket_waiting_reminder_due: "Remind a client we are waiting for their reply.",
    ticket_pending_expired: "Act on a ticket whose client never replied in the time allowed.",
    ticket_resolved: "Start when a ticket is marked resolved, for example to ask for a rating.",
    ticket_reopened: "React when a resolved ticket is opened again.",
    ticket_auto_close_due: "Close resolved tickets after the days the workspace allows.",
    ticket_csat_received: "React when a client rates a resolved ticket.",
    ticket_intake_requested: "Guide a client through opening a ticket in the chat.",
    order_placed: "React when an order is placed, or an order request is taken in a workspace without an order API.",
    order_status_changed: "React when the order system moves an order to a new status.",
    order_approval_requested: "React when an order is above a limit and waits for an approver.",
    order_approval_decided: "React when an approval ends: approved, declined, expired or withdrawn.",
    order_sync_failed: "React when an order could not be written to the order system after several tries.",
    quote_status_changed: "React when a quote is asked for, priced, accepted, declined or expires.",
    visit_scheduled: "React when a visit is booked or moved to a new time.",
    visit_status_changed: "React when a visit is dispatched, started, completed, cancelled or missed.",
    visit_reminder_due: "React when it is time to remind the client or the engineer of a visit.",
    asset_maintenance_due: "React when preventive maintenance or a calibration falls due on an asset.",
    asset_warranty_expiring: "React when the warranty of an asset is about to end.",
    contract_expiring: "React when a service contract is about to end.",
    rma_status_changed: "React when a return moves to a new status.",
    attribute_changed: "React when a conversation, visitor or lead attribute changes.",
    visitor_clicks_element: "Start a message when a visitor clicks a button or link on your site.",
    platform_event: "Runs for every event your endpoints can receive. It belongs to the Developer webhooks default workflow.",
    handoff_reminder_due: "Remind the people handling a handoff that nobody has replied yet.",
    handoff_escalation_due: "Tell the Owners and Admins that a handoff has waited too long.",
    handoff_waiting_digest_due: "Send the morning summary of handoffs that arrived out of office hours.",
    conversation_returned_to_queue: "Alert the team when a member leaves and their conversations go back to the queue.",
    lead_followed_up: "Tell the owner when a visitor asks again in the chat of an existing lead.",
    gap_assigned: "Follow up when someone is given a knowledge gap to answer.",
    connector_event_received: "Start when a connected system tells Sagot that something happened, such as an order shipped. Needs the Pro plan.",
  },
  options: {
    attribute: "Attribute to watch",
    attributeChoose: "Choose an attribute",
    attributeGroups: { conversation: "Conversation attributes", visitor: "Visitor attributes", lead: "Lead attributes" },
    attributeGone: "{{label}} (no longer defined)",
    attributeHint:
      "Starts when this attribute changes: edited in the Inbox or on a lead, or sent from your site with Sagot('identify'). A change a workflow makes never starts a workflow, so there are no loops. The same value seen again within 10 seconds starts it once.",
    attributeFacts: "Conditions can use “Attribute”, “Previous value” and “New value”. Messages and notes can use the variables event.previous and event.value.",
    attributeNone: "No attributes are defined yet. Define one first, then come back.",
    attributeNoneLink: "Define an attribute",
    attributeNoConversation: "A visitor or lead attribute has no conversation behind it, so steps that act on a conversation (tags, status, notes, snooze …) can't be used.",
    selector: "Element to click",
    selectorPlaceholder: "#apply-now, .tuition-cta",
    selectorHint: "A CSS selector for the element on your page. Separate up to 5 with commas. Allowed: tags, #id, .class, [attribute=value], and a space or > between them.",
    selectorCount_one: "{{count}} selector understood",
    selectorCount_other: "{{count}} selectors understood",
    selectorTest: "Test it on your page",
    selectorTestBody: "Open your page, open the browser console and run the line below. It should list the element you mean.",
    clickFrequency: "How often it can start",
    clickFrequencies: {
      every_click: "Every click (at most once every 30 seconds)",
      once: "Once per visitor",
      every_session: "Once per browsing session",
    },
    widgetOnly: "This trigger works in the website widget only. Mobile apps, Messenger and e-mail never report clicks.",
    startTitle: "Before you create it",
  },
  conditionFields: {
    event_changed_by: "Changed by",
    event_attribute: "Attribute",
    event_previous: "Previous value",
    event_value: "New value",
    event_actor_user_id: "Member who did it",
    event_changed: "Fields that changed",
    source_notice: "How the sync is reported",
    source_run_kind: "What started the sync",
    office_open: "Office is open",
    connector_event: "Event name",
    connector_id: "Connector",
    connector_hook: "Hook",
  },
  conditionChoices: {
    choose: "Choose",
event_changed_by: { staff: "A team member", assistant: "The assistant", system: "The system (a workflow or an automation)", visitor: "The visitor (a booking they made)" },
    source_notice: { none: "Nobody", in_app: "In the app only", email: "By e-mail only", in_app_email: "In the app and by e-mail" },
    source_run_kind: { first: "The first sync", manual: "A re-sync by a person", scheduled: "A scheduled re-crawl" },
  },
  problems: {
    needs_attribute: "Choose which attribute this workflow watches.",
    needs_selector: "Choose which element a click on the page starts this workflow.",
    selector_syntax: "The element selector is not valid. Use tags, #ids, .classes and [attribute=value], for example #apply-now, .tuition-cta.",
    selector_unsupported: "The element selector uses something that is not allowed: pseudo-classes such as :has() or :nth-child, sibling combinators, * or attribute matchers like *=.",
    selector_too_many: "Use at most 5 selectors, separated by commas.",
    selector_too_long: "The element selector is at most 200 characters.",
    clicks_widget_only: "A click starts a workflow only in the website widget. Remove the other channels.",
    bad_frequency: "The frequency {{frequency}} does not apply to this trigger.",
  },
  attributeValues: {
    title: "Attributes",
    objects: { conversation: "This conversation", visitor: "The visitor", lead: "This lead" },
    save: "Save attributes",
    saved: "Attributes saved.",
    notSet: "Not set",
    listPlaceholder: "Separate with commas",
    workflowHint: "Workflows that watch an attribute run when it changes.",
    loadFailed: "We couldn't load the attributes.",
  },
} as const;
